import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import CategoryCard from '@/components/base/CategoryCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { comics, categories } from '@/mocks/comics';

const sortOptions = [
  { value: 'latest', label: '最新' },
  { value: 'rating', label: '评分最高' },
  { value: 'views', label: '人气最旺' },
];

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState('latest');
  const [activeStatus, setActiveStatus] = useState<string | null>(null);

  const currentCategory = categories.find((c) => c.slug === slug);

  const categoryComics = useMemo(() => {
    let filtered = slug ? comics.filter((c) => c.categorySlug === slug) : comics;
    if (activeStatus) {
      filtered = filtered.filter((c) => c.status === activeStatus);
    }
    switch (sortBy) {
      case 'rating':
        return [...filtered].sort((a, b) => b.rating - a.rating);
      case 'views':
        return [...filtered].sort((a, b) => b.views - a.views);
      default:
        return filtered;
    }
  }, [slug, sortBy, activeStatus]);

  const catName = currentCategory?.name || '全部漫画';
  const catDesc = currentCategory?.description || '探索yy漫画网全部韩漫免费漫画资源，随时随地畅享下拉式在线阅读体验。';

  const catSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://yymanhua.com' },
          { '@type': 'ListItem', position: 2, name: catName, item: `https://yymanhua.com/category/${slug}` },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${catName} - yy漫画网`,
        description: catDesc,
        url: `https://yymanhua.com/category/${slug}`,
        isPartOf: { '@type': 'WebSite', name: 'yy漫画网', url: 'https://yymanhua.com' },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${catName} - 韩漫免费漫画在线阅读 - yy漫画网`}
        description={`${catDesc} yy漫画网为您提供海量${catName}类韩漫免费漫画，支持下拉式在线阅读，更新及时画质清晰。`}
        keywords={`${catName},韩漫免费漫画,在线阅读下拉式韩国漫画,yy漫画网,韩国漫画`}
        canonicalUrl={`/category/${slug}`}
        schemaJson={catSchema}
      />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100/70">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-8">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs" />
              <span className="text-foreground-900 font-medium">{catName}</span>
            </nav>
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">{catName}</h1>
            <p className="text-sm text-foreground-500">{catDesc}</p>
          </div>
        </div>

        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-8">
          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-8">
            <Link
              to="/category/lianai"
              className={`flex flex-col items-center gap-2 px-4 py-3 rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap ${
                !slug ? 'bg-primary-500 text-white' : 'bg-background-100 hover:bg-primary-100 text-foreground-700 hover:text-primary-600'
              }`}
            >
              <div className={`w-10 h-10 flex items-center justify-center rounded-full ${!slug ? 'bg-white/20' : 'bg-background-200'}`}>
                <i className="ri-apps-line text-lg" />
              </div>
              <span className="text-xs font-medium">全部</span>
              <span className={`text-[10px] ${!slug ? 'text-white/70' : 'text-foreground-400'}`}>
                {comics.length}部
              </span>
            </Link>
            {categories.map((cat) => (
              <CategoryCard key={cat.slug} category={cat} active={slug === cat.slug} />
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActiveStatus(null)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  !activeStatus ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-primary-100'
                }`}
              >
                全部
              </button>
              <button
                onClick={() => setActiveStatus('连载中')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  activeStatus === '连载中' ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-primary-100'
                }`}
              >
                连载中
              </button>
              <button
                onClick={() => setActiveStatus('已完结')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  activeStatus === '已完结' ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-primary-100'
                }`}
              >
                已完结
              </button>
            </div>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    sortBy === opt.value ? 'bg-white text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {categoryComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {categoryComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-file-search-line text-2xl text-foreground-400" />
              </div>
              <p className="text-foreground-500">暂无符合条件的漫画</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}